import './ByzzerOshMarketSelect.scss';
import { forwardRef, useEffect, useState, useCallback, useMemo } from 'react';
import ByzzerOshMarketCheckbox, { TreeNode } from './ByzzerOshMarketCheckbox';
import { ByzzerMask } from '../ByzzerMask/ByzzerMask';
import { ByzzerChangeEvent, ByzzerChangeEventHandler, ByzzerSearch, ByzzerSelectOption } from '@byzzer/ui-components';
import { FilterSection } from '../ConfigurationEditors/FilterGroup';
import { SelectorLabelInfo } from '../SelectorLabelInfo';
import { useTenantApi } from '@/hooks';
import { RunConfigMarket } from '@/types/ReportRun';

type ByzzerOshMarketSelectProps = {
    includeOmniShopperMarkets: boolean;
    sku: string;
    value: Pick<RunConfigMarket, 'name' | 'key'>[];
    onChange?: ByzzerChangeEventHandler<{ value: Pick<RunConfigMarket, 'name' | 'key'>[] }>;
    disabled?: boolean;
    maxSelections?: number;
    name: string;
};

type MarketValue = {
    value: string;
    type: string;
    display: string;
};

type SearchResult = {
    display: string;
    value: string;
    metaData: { type: string };
};

type MarketOptions = {
    totalMarkets: TreeNode[];
    outlets: TreeNode[];
};

const baseClassName = 'osh-market-select';

export const ByzzerOshMarketSelect = forwardRef<HTMLDivElement, ByzzerOshMarketSelectProps>(
    ({ includeOmniShopperMarkets, sku, value, onChange, name, maxSelections }, ref) => {
        const { getOmnishopperMarkets } = useTenantApi();

        const [isLoading, setIsLoading] = useState(false);
        const [marketOptions, setMarketOptions] = useState<MarketOptions>({
            totalMarkets: [],
            outlets: [],
        });
        const [internalValue, setInternalValue] = useState<MarketValue[]>([]);
        const [disabledMarkets, setDisabledMarkets] = useState<string[]>([]);

        const determineDisabledOptions = useCallback(
            (data: MarketValue[]) => {
                let disabledMarkets: string[] = [];
                const outlets = marketOptions.outlets.flatMap((item) => item.children ?? []);
                const allMarkets = [...marketOptions.totalMarkets, ...outlets];

                if (maxSelections && data.length === maxSelections) {
                    disabledMarkets = allMarkets
                        .filter((item) => !data.some((selected) => selected.value === item.value))
                        .map((item) => item?.value || '');
                } else if (data.some((item) => item.type === 'total_outlets')) {
                    disabledMarkets = allMarkets
                        .filter((item) => item?.metaData?.type !== 'total_outlets')
                        .map((item) => item?.value || '');
                } else if (data.some((item) => item.type === 'channel')) {
                    disabledMarkets = allMarkets
                        .filter((item) => item?.metaData?.type !== 'channel')
                        .map((item) => item?.value || '');
                } else if (data.some((item) => item.type === 'outlet')) {
                    disabledMarkets = allMarkets
                        .filter((item) => item?.metaData?.type !== 'outlet')
                        .map((item) => item?.value || '');
                }
                setDisabledMarkets(disabledMarkets);
            },
            [marketOptions]
        );

        const searchMarkets = useCallback(
            async (searchTerm: string): Promise<SearchResult[]> => {
                if (!searchTerm) return [];

                const outlets = marketOptions.outlets.flatMap((item) => item.children ?? []);
                const totalMarkets = marketOptions.totalMarkets;
                return [...totalMarkets, ...outlets]
                    .filter(
                        (item): item is TreeNode =>
                            !!item?.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((item) => ({
                        display: item.name,
                        value: item.value,
                        metaData: { type: item.metaData?.type ?? '' },
                    }));
            },
            [marketOptions]
        );

        const transformToTreeData = useCallback((market: {label: string, value?: string, options?: string[]}, flatTree: boolean = false): TreeNode => {
            return {
                name: market.label,
                id: market.label.toLowerCase().replace(/\s+/g, '-'),
                isFolder: flatTree ? false : true,
                isSelectable: flatTree ? true : false,
                isExpanded: false,
                value: market?.value ?? market.label, // it would be nice to use TS to let it know if it should expect a value or not.  when outlets are passed in, it will be undefined which is Ok.  
                metaData: flatTree ? { type: market.label === 'Total Outlets' ? 'total_outlets' : 'channel' } : {},
                children: (market?.options ?? [])
                    .toSorted((a: string, b: string) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
                    .map((option: string) => ({
                        name: option,
                        id: option.toLowerCase().replace(/\s+/g, '-'),
                        isFolder: false,
                        isSelectable: true,
                        isExpanded: false,
                        value: option,
                        metaData: { type: 'outlet' },
                    })),
            };
        }, []);

        const handleChange = (node: TreeNode, checked: boolean) => {
            setInternalValue((prev) => {
                if (checked && maxSelections && prev.length >= maxSelections) {
                    return prev;
                }

                const newValue = checked
                    ? [
                        ...prev, 
                        { 
                            value: node.value, 
                            display: node.name, 
                            type: node.metaData?.type
                        }
                    ]
                    : prev.filter((item) => item.value !== node.value);

                determineDisabledOptions(newValue);
                onChange?.({
                    name: name, //@ts-ignore
                    value: newValue.map((item) => ({ name: item.display, key: item.value, selectionType: item.type })),
                });
                return newValue;
            });
        };

        const handleSearchChange = (event: ByzzerChangeEvent<(string | ByzzerSelectOption)[]>) => {
            if (!Array.isArray(event.value)) return;

            const newValue = event.value.map((item) => {
                const type =
                    ((item as unknown) as SearchResult)?.metaData?.type ||
                    (item as ByzzerSelectOption)?.data?.metaData?.type ||
                    '';
                return {
                    value: typeof item === 'string' ? item : item.value,
                    display: typeof item === 'string' ? item : item.display,
                    type: type,
                };
            });

            setInternalValue(newValue);
            determineDisabledOptions(newValue);
            onChange?.({
                name: name, //@ts-ignore
                value: newValue.map((item) => ({ name: item.value, key: item.value, selectionType: item.type })),
            });
        };

        useEffect(() => {
            (async () => {
                try {
                    setIsLoading(true);
                    const oshMarkets = await getOmnishopperMarkets();
                    if (!oshMarkets) return;

                    const transformedTotalMarkets = oshMarkets?.totalMarkets.map((marketName) =>
                        transformToTreeData(
                            {
                                label: marketName === 'Total Outlets' ? marketName : `Total ${marketName}`,
                                value: marketName // this is the value that we need for reporting and for validation.  cannot use the combo of 'Total' + marketName since BiqQuery doesn't have it stored that way.
                            },
                            true
                        )
                    );
                    const transformedOutlets = oshMarkets?.outlets.map((market) => transformToTreeData(market)); // 'market' doesnt have a display(label)/value concept but that's ok here since we're not appending anything to the label.
                    setMarketOptions((preVal) => ({
                        totalMarkets: transformedTotalMarkets ?? [],
                        outlets: transformedOutlets ?? [],
                    }));
                } catch (error) {
                    console.error('Error fetching omnishopper markets');
                    setMarketOptions({ totalMarkets: [], outlets: [] });
                } finally {
                    setIsLoading(false);
                }
            })();
        }, []);

        useEffect(() => {
            if (value) {
                const outlets = marketOptions.outlets.flatMap((item) => item.children ?? []);
                const allMarkets = [...marketOptions.totalMarkets, ...outlets];
                const selectedMarkets = allMarkets
                    .filter((item) => value.some((selected) => selected.key === item.value))
                    .map((item) => ({
                        value: item.value,
                        type: item.metaData?.type ?? '',
                        display: item.name
                    }));
                setInternalValue(() => {
                    determineDisabledOptions(selectedMarkets);
                    return selectedMarkets;
                });
            }
        }, [value, marketOptions]);

        const searchValue = useMemo(
            () =>
                internalValue.map((item) => ({
                    display: item.display,
                    value: item.value,
                    metaData: { type: item.type },
                })),
            [internalValue]
        );

        return (
            <FilterSection onlyRenderIf={includeOmniShopperMarkets}>
                <div className={baseClassName}>
                    <ByzzerMask loading={isLoading} className={`${baseClassName}__mask`} />

                    <ByzzerSearch
                        name={name}
                        className={`${baseClassName}__search`}
                        search={searchMarkets}
                        onChange={handleSearchChange}
                        value={searchValue}
                        excludedOptions={disabledMarkets}
                    />

                    {marketOptions.totalMarkets.length > 0 && (
                        <>
                            <SelectorLabelInfo
                                className="osh-market-select__label"
                                defaultLabel="Total Markets"
                                sku={sku as string}
                                selectorCode={'oshTotalMarkets'}
                                isLabelBold={true}
                            />
                            <div className={`${baseClassName}__tree--total-markets`}>
                                {marketOptions.totalMarkets.map((node) => (
                                    <ByzzerOshMarketCheckbox
                                        key={node.id}
                                        node={node}
                                        value={internalValue.map((item) => item.value)}
                                        onSelect={handleChange}
                                        disabledMarkets={disabledMarkets}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    {marketOptions.outlets.length > 0 && (
                        <>
                            <SelectorLabelInfo
                                className="osh-market-select__label"
                                defaultLabel="Outlets By Channel"
                                sku={sku as string}
                                selectorCode={'oshMarketOutlets'}
                                isLabelBold={true}
                            />
                            <div className={`${baseClassName}__tree-outlets`}>
                                {marketOptions.outlets.map((node) => (
                                    <ByzzerOshMarketCheckbox
                                        key={node.id}
                                        node={node}
                                        value={internalValue.map((item) => item.value)}
                                        onSelect={handleChange}
                                        disabledMarkets={disabledMarkets}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </FilterSection>
        );
    }
);

ByzzerOshMarketSelect.displayName = 'ByzzerOshMarketSelect';